<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { radioKeyTarget } from '@/domain/store-setup/business-step';
  import {
    BUSINESS_TYPES,
    PRIMARY_BUSINESS_TYPES,
  } from '@/domain/store-setup/business-types';
  import type { TBusinessType } from '@/interfaces/IStoreSetup';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «¿Qué tipo de negocio es?» (plan §4.2 item 3, D8): one choice among 17
   * chips, 11 visible and 6 behind «Ver más». A radiogroup: arrow keys move
   * the focus, Space picks. Never blocking (default `restaurant`).
   */
  const props = withDefaults(
    defineProps<{
      value: TBusinessType;
      fromGoogle: boolean;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{ (e: 'input', code: TBusinessType): void }>();

  const labelId = nextDomId('setup-business-type');
  const root = ref<HTMLElement | null>(null);

  function isPrimary(code: TBusinessType): boolean {
    return PRIMARY_BUSINESS_TYPES.includes(code);
  }

  /** Open from the start when the current type is one of the extra six. */
  const expanded = ref(!isPrimary(props.value));
  /** The chip that takes the Tab stop (roving tabindex). */
  const focusCode = ref<TBusinessType>(props.value);

  const visibleCodes = computed(() =>
    expanded.value ? BUSINESS_TYPES : PRIMARY_BUSINESS_TYPES
  );
  const tabStop = computed(() =>
    visibleCodes.value.includes(focusCode.value) ? focusCode.value : props.value
  );

  async function focusChip(code: TBusinessType) {
    focusCode.value = code;
    await nextTick();
    const chip = root.value?.querySelector(`[data-code="${code}"]`);
    if (chip instanceof HTMLElement) chip.focus();
  }

  function select(code: TBusinessType) {
    if (props.disabled) return;
    focusCode.value = code;
    if (code !== props.value) emit('input', code);
  }

  function onKeydown(event: KeyboardEvent, code: TBusinessType) {
    const codes = visibleCodes.value;
    const target = radioKeyTarget(codes.indexOf(code), event.key, codes.length);
    if (target === null) return;
    event.preventDefault();
    void focusChip(codes[target]);
  }

  function showMore() {
    expanded.value = true;
    // The button goes away: the focus moves to the first new chip.
    void focusChip(BUSINESS_TYPES[PRIMARY_BUSINESS_TYPES.length]);
  }

  watch(
    () => props.value,
    (code) => {
      focusCode.value = code;
      if (!isPrimary(code)) expanded.value = true;
    }
  );
</script>

<template>
  <div ref="root" class="v2-setup-business-types">
    <p :id="labelId" class="types-label">
      {{ $t('storeSetup.business.typeQuestion') }}
      <span v-if="fromGoogle" class="types-source">{{
        $t('storeSetup.business.fromGoogle')
      }}</span>
    </p>
    <div class="types-chips" role="radiogroup" :aria-labelledby="labelId">
      <PosUiChip
        v-for="code in BUSINESS_TYPES"
        v-show="expanded || isPrimary(code)"
        :key="code"
        radio
        :active="code === value"
        :disabled="disabled"
        :data-code="code"
        :tabindex="code === tabStop ? 0 : -1"
        @click="select(code)"
        @keydown.native="onKeydown($event, code)"
        >{{ $t(`storeSetup.business.types.${code}`) }}</PosUiChip
      >
    </div>
    <button
      v-show="!expanded"
      type="button"
      class="types-more"
      :disabled="disabled"
      @click="showMore"
    >
      {{ $t('storeSetup.business.typeMore') }}
      <PosUiIcon name="chevron-down" class="more-icon" />
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-business-types {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-2;
    .types-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .types-source {
      @apply tw-text-xs tw-font-normal tw-text-neutral-500;
    }
    .types-chips {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .types-more {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .more-icon {
      @apply tw-text-base tw-no-underline;
    }
  }
</style>
